import React, {useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {IoIosInformationCircle, IoIosLock} from "react-icons/io";
import {IoChevronDown, IoPlay} from "react-icons/io5";
import {TbBrandGithubFilled} from "react-icons/tb";
import {projects, Project} from "../data/projects";
import {Tags} from "./projectBits";
import {EarthPlacePage} from "./earthPlace/earthPlacePage";
import {DocPage} from "./earthPlace/docPage";
import {privacyIntro, privacySections, privacyUpdated} from "./earthPlace/privacy";
import {supportIntro, supportSections, supportUpdated} from "./earthPlace/support";
import {FridgePage} from "./fridgePage";
import {TrailblazerPage} from "./trailblazerPage";
import placeholder from "../assets/projects/placeholder.svg";

interface ProjectsProps {
    className?: string;
    // the part of the path after /projects/, when on a sub-page
    sub?: string;
}

export function Projects({className, sub}: ProjectsProps) {
    return (
        <div className={className} id={"projects"}>
            {sub ? <ProjectSubpage sub={sub}/> : <ProjectIndex/>}
        </div>
    );
}

function ProjectSubpage({sub}: {sub: string}) {
    if (sub === "earthplace") return <EarthPlacePage/>;
    if (sub === "earthplace/privacy") {
        return <DocPage title="earthPlace privacy policy" updated={privacyUpdated} intro={privacyIntro}
                        sections={privacySections} crossLink={{to: "/projects/earthplace/support", label: "Support"}}/>;
    }
    if (sub === "earthplace/support") {
        return <DocPage title="earthPlace support" updated={supportUpdated} intro={supportIntro}
                        sections={supportSections} crossLink={{to: "/projects/earthplace/privacy", label: "Privacy policy"}}/>;
    }
    if (sub === "fridge") return <FridgePage/>;
    if (sub === "trailblazer") return <TrailblazerPage/>;
    return null;
}

function ProjectIndex() {
    const hero = projects.filter(p => p.tier === "hero");
    const features = projects.filter(p => p.tier === "feature");
    const rest = projects.filter(p => p.tier === "list");
    return (
        <>
            {hero.map(p => <ProjectCard key={p.id} project={p}/>)}
            <div className="pj-features">
                {features.map(p => <ProjectCard key={p.id} project={p}/>)}
            </div>
            <h3>More projects</h3>
            <hr/>
            <div className="pj-list">
                {rest.map(p => <ProjectRow key={p.id} project={p}/>)}
            </div>
        </>
    );
}

// Hero and feature cards: the whole card opens the project's page.
function ProjectCard({project}: {project: Project}) {
    const navigate = useNavigate();
    const to = `/projects/${project.page}`;
    const stop = (e: React.MouseEvent) => e.stopPropagation();
    return (
        <div className={`pj-card pj-${project.tier}`} onClick={() => navigate(to)}>
            {project.thumbnail && <img className="pj-card-image" src={project.thumbnail} alt="" loading="lazy"
                                          style={{objectPosition: project.thumbnailPosition}}/>}
            <div className="pj-card-body">
                <h3>
                    <Link to={to} onClick={stop}>{project.title}</Link>
                    <span className="pj-date">{[project.date, project.status].filter(Boolean).join(" ")}</span>
                </h3>
                <p className="pj-muted">{project.tagline}</p>
                {project.blurb && <p>{project.blurb}</p>}
                <div className="pj-card-foot">
                    <Tags tags={project.stack}/>
                    {project.cta === "try" && (
                        <Link className="button pj-act" to={`${to}#try-it-out`} onClick={stop}><IoPlay/> Try it</Link>
                    )}
                    {project.cta === "learn" && (
                        <Link className="button pj-act" to={to} onClick={stop}><IoIosInformationCircle/> Learn more</Link>
                    )}
                </div>
            </div>
        </div>
    );
}

// "More projects" rows: a GitHub button if the source is public, else a lock.
// Clicking a row expands it to show the full write-up.
function ProjectRow({project}: {project: Project}) {
    const [open, setOpen] = useState(false);
    return (
        <div className={`pj-row${open ? " open" : ""}`} onClick={() => setOpen(o => !o)}
             role="button" tabIndex={0} aria-expanded={open}
             onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(o => !o); } }}>
            {project.thumbnail
                ? <img className="pj-row-thumb" src={project.thumbnail} alt="" loading="lazy"/>
                : <img className="pj-row-thumb placeholder" src={placeholder} alt="" loading="lazy"/>}
            <div className="pj-row-text">
                <h4>{project.title}</h4>
                <p className="pj-small pj-muted">{project.tagline}</p>
                <Tags tags={project.stack}/>
            </div>
            <div className="pj-row-meta">
                <span className="pj-row-date">{project.date}{project.status && <><br/>{project.status}</>}</span>
                {project.source ? (
                    <a className="button pj-access" href={project.source} target="_blank" rel="noopener noreferrer"
                       aria-label={`${project.title} source on GitHub`} onClick={(e) => e.stopPropagation()}>
                        <TbBrandGithubFilled/>
                    </a>
                ) : (
                    <span className="pj-access pj-lock" title="Closed source" aria-label="Closed source"><IoIosLock/></span>
                )}
                <span className="pj-chevron"><IoChevronDown/></span>
            </div>
            <div className="pj-row-more">
                <div className="pj-row-more-inner">
                    <hr/>
                    <ul>{project.description.map((line, i) => <li key={i}>{line}</li>)}</ul>
                </div>
            </div>
        </div>
    );
}
